// src/hooks/useColorState.ts
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  COLOR_STORAGE_KEY, 
  DEFAULT_COLOR,
  getColorPathForLanguage, 
  getColorHexFromPath 
} from '@/config/colors';
import { useLocalization } from './useLocalization';

export const useColorState = (initialPath?: string) => {
  const navigate = useNavigate();
  const { currentLang } = useLocalization();
  const [selectedColor, setSelectedColor] = useState(() => {
    // Try to get color from localStorage
    const storedColor = localStorage.getItem(COLOR_STORAGE_KEY);
    if (storedColor) return storedColor;

    // Try to get color from URL path
    if (initialPath) {
      const pathColor = getColorHexFromPath(initialPath, currentLang);
      if (pathColor !== DEFAULT_COLOR) return pathColor;
    }

    return DEFAULT_COLOR;
  });

  useEffect(() => {
    localStorage.setItem(COLOR_STORAGE_KEY, selectedColor);
  }, [selectedColor]);

  const changeColor = useCallback((newColor: string) => {
    setSelectedColor(newColor);
    localStorage.setItem(COLOR_STORAGE_KEY, newColor);

    const newPath = getColorPathForLanguage(newColor, currentLang);
    const finalPath = currentLang === 'en' 
      ? `/${newPath}` 
      : `/${currentLang}/${newPath}`;

    navigate(finalPath, { replace: true });
  }, [navigate, currentLang]);

  const updateColorFromPath = useCallback((path: string) => {
    const pathColor = getColorHexFromPath(path, currentLang);
    if (pathColor !== selectedColor) {
      setSelectedColor(pathColor);
      localStorage.setItem(COLOR_STORAGE_KEY, pathColor);
    }
  }, [currentLang, selectedColor]);

  return {
    selectedColor,
    changeColor,
    updateColorFromPath
  };
};